import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { PageHeader, BtnSecondary, Empty } from "@/components/primitives";
import { toast } from "sonner";
import { Bell, Warning, Info, CheckSquare, ArrowSquareOut } from "@phosphor-icons/react";

const ICON = { stock: Warning, approval: CheckSquare, maintenance: Bell, system: Info };
const COLOR = { info: "#002FA7", warning: "#FFC000", danger: "#FF2A2A" };

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();

  async function load() {
    try { const { data } = await api.get("/notifications"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function markRead(id) {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function markAll() {
    try { await api.post("/notifications/read-all"); toast.success("All marked read"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function openAction(n) {
    // mark read in background, then navigate
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) {
      // Internal route — start with /
      if (n.link.startsWith("http")) {
        window.open(n.link, "_blank", "noopener");
      } else {
        nav(n.link);
      }
    } else {
      // Fallback inferred deep-links
      const fallback = inferLink(n);
      if (fallback) nav(fallback);
      else toast.message("No action target attached to this notification");
    }
  }

  function inferLink(n) {
    const cat = n.category;
    if (cat === "stock") return "/inventory";
    if (cat === "approval") return "/purchase-orders";
    if (cat === "maintenance") return "/machines";
    return null;
  }

  return (
    <div data-testid="notif-page">
      <PageHeader
        overline="Module / Alerts"
        title="Notifications"
        subtitle="Click any alert to jump straight to the action it needs."
        actions={<BtnSecondary onClick={markAll} data-testid="notif-read-all">Mark all read</BtnSecondary>}
      />

      <div className="space-y-2">
        {rows.length === 0 && <Empty label="You're all caught up." />}
        {rows.map((n) => {
          const Icon = ICON[n.category] || Info;
          const targetLabel = n.link || (n.category && {
            stock: "/inventory",
            approval: "/purchase-orders",
            maintenance: "/machines",
          }[n.category]) || null;
          return (
            <div
              key={n.id}
              data-testid={`notif-row-${n.id}`}
              onClick={() => openAction(n)}
              className={`phizo-card flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-[#fafafa] ${n.read ? "opacity-60" : ""}`}
            >
              <div
                className="h-10 w-10 flex items-center justify-center flex-shrink-0"
                style={{ background: COLOR[n.severity] || "#002FA7" }}
              >
                <Icon size={20} weight="bold" color="#fff" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-[#09090b] flex items-center gap-2">
                    {n.title}
                    <ArrowSquareOut size={14} weight="bold" className="text-[#a1a1aa]" />
                  </div>
                  <div className="text-xs text-[#a1a1aa] mono">{fmtDateTime(n.created_at)}</div>
                </div>
                <div className="text-sm text-[#52525b] mt-1">{n.body}</div>
                <div className="flex items-center justify-between mt-2 gap-3">
                  {targetLabel ? (
                    <div className="text-[10px] mono text-[#a1a1aa] truncate">
                      → {targetLabel}
                    </div>
                  ) : <span />}
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      data-testid={`notif-read-${n.id}`}
                      className="text-xs font-semibold text-[#002FA7] hover:underline">
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
